import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, onSnapshot, where, orderBy, limit, doc, getDocs } from 'firebase/firestore';

// Helper function to format timestamp with ultra-hot styling
const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    let date;
    if (typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
    } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
    } else {
        return 'Invalid Date';
    }

    if (isNaN(date.getTime())) {
        return 'Invalid Date';
    }

    const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    };
    return date.toLocaleString('en-US', options);
};

// Helper function to get relative time for ultra-interactive display
const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    
    let date;
    if (typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
    } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
    } else {
        return '';
    }

    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatTimestamp(timestamp);
};

// Enhanced helper function to get ultra-interactive topic colors with gradients
const getTopicColorAndIcon = (topic) => {
    if (!topic) return { color: 'bg-gray-400', gradient: 'from-gray-500 to-gray-600', icon: 'fas fa-circle', glow: 'shadow-gray-500/25' };
    
    const topicData = {
        'command': { 
            color: 'bg-blue-400', 
            gradient: 'from-blue-500 to-cyan-500', 
            icon: 'fas fa-terminal',
            glow: 'shadow-blue-500/25',
            pulse: 'animate-pulse'
        },
        'revive': { 
            color: 'bg-green-400', 
            gradient: 'from-green-500 to-emerald-500', 
            icon: 'fas fa-heart',
            glow: 'shadow-green-500/25',
            pulse: 'animate-heartbeat'
        },
        'ac - detections': { 
            color: 'bg-red-400', 
            gradient: 'from-red-500 to-rose-500', 
            icon: 'fas fa-shield-alt',
            glow: 'shadow-red-500/25',
            pulse: 'animate-bounce'
        },
        'ac-admin': { 
            color: 'bg-red-500', 
            gradient: 'from-red-600 to-pink-600', 
            icon: 'fas fa-user-shield',
            glow: 'shadow-red-500/30',
            pulse: 'animate-glow'
        },
        'ac - bans': { 
            color: 'bg-red-600', 
            gradient: 'from-red-700 to-red-500', 
            icon: 'fas fa-ban',
            glow: 'shadow-red-600/35',
            pulse: 'animate-pulse-slow'
        },
        'player_join': { 
            color: 'bg-green-500', 
            gradient: 'from-green-600 to-lime-500', 
            icon: 'fas fa-user-plus',
            glow: 'shadow-green-500/25',
            pulse: 'animate-bounceIn'
        },
        'player_leave': { 
            color: 'bg-yellow-500', 
            gradient: 'from-yellow-500 to-orange-400', 
            icon: 'fas fa-user-minus',
            glow: 'shadow-yellow-500/25',
            pulse: 'animate-fadeIn'
        },
        'item_give': { 
            color: 'bg-purple-400', 
            gradient: 'from-purple-500 to-violet-500', 
            icon: 'fas fa-gift',
            glow: 'shadow-purple-500/25',
            pulse: 'animate-float'
        },
        'item_remove': { 
            color: 'bg-orange-400', 
            gradient: 'from-orange-500 to-red-400', 
            icon: 'fas fa-trash',
            glow: 'shadow-orange-500/25',
            pulse: 'animate-pulse'
        },
        'money_deposit': { 
            color: 'bg-emerald-400', 
            gradient: 'from-emerald-500 to-green-400', 
            icon: 'fas fa-coins',
            glow: 'shadow-emerald-500/25',
            pulse: 'animate-shimmer'
        },
        'money_withdraw': { 
            color: 'bg-amber-400', 
            gradient: 'from-amber-500 to-yellow-400', 
            icon: 'fas fa-hand-holding-usd',
            glow: 'shadow-amber-500/25',
            pulse: 'animate-glow'
        },
        'vehicle_spawn': { 
            color: 'bg-cyan-400', 
            gradient: 'from-cyan-500 to-blue-400', 
            icon: 'fas fa-car',
            glow: 'shadow-cyan-500/25',
            pulse: 'animate-slideInLeft'
        },
        'vehicle_despawn': { 
            color: 'bg-gray-500', 
            gradient: 'from-gray-600 to-slate-500', 
            icon: 'fas fa-car-crash',
            glow: 'shadow-gray-500/25',
            pulse: 'animate-fadeIn'
        },
        'job_change': { 
            color: 'bg-indigo-400', 
            gradient: 'from-indigo-500 to-blue-500', 
            icon: 'fas fa-briefcase',
            glow: 'shadow-indigo-500/25',
            pulse: 'animate-bounceIn'
        },
        'gang_activity': { 
            color: 'bg-pink-400', 
            gradient: 'from-pink-500 to-rose-500', 
            icon: 'fas fa-users',
            glow: 'shadow-pink-500/25',
            pulse: 'animate-pulse'
        },
        'admin_kick': { 
            color: 'bg-red-400', 
            gradient: 'from-red-500 to-orange-500', 
            icon: 'fas fa-user-times',
            glow: 'shadow-red-500/25',
            pulse: 'animate-bounce'
        },
        'admin_ban': { 
            color: 'bg-red-600', 
            gradient: 'from-red-700 to-pink-600', 
            icon: 'fas fa-gavel',
            glow: 'shadow-red-600/30',
            pulse: 'animate-glow'
        },
        'error_log': { 
            color: 'bg-red-500', 
            gradient: 'from-red-600 to-rose-500', 
            icon: 'fas fa-exclamation-triangle',
            glow: 'shadow-red-500/30',
            pulse: 'animate-bounce'
        },
        'warning_log': { 
            color: 'bg-yellow-400', 
            gradient: 'from-yellow-500 to-amber-400', 
            icon: 'fas fa-exclamation',
            glow: 'shadow-yellow-500/25',
            pulse: 'animate-pulse'
        },
        'chat_message': { 
            color: 'bg-blue-300', 
            gradient: 'from-blue-400 to-sky-400', 
            icon: 'fas fa-comment',
            glow: 'shadow-blue-400/25',
            pulse: 'animate-fadeIn'
        },
        'resource_start': { 
            color: 'bg-green-400', 
            gradient: 'from-green-500 to-lime-500', 
            icon: 'fas fa-play',
            glow: 'shadow-green-500/25',
            pulse: 'animate-slideInRight'
        },
        'resource_stop': { 
            color: 'bg-red-400', 
            gradient: 'from-red-500 to-pink-500', 
            icon: 'fas fa-stop',
            glow: 'shadow-red-500/25',
            pulse: 'animate-fadeIn'
        },
        'database_error': { 
            color: 'bg-red-600', 
            gradient: 'from-red-700 to-red-500', 
            icon: 'fas fa-database',
            glow: 'shadow-red-600/35',
            pulse: 'animate-bounce'
        }
    };
    
    return topicData[topic] || { 
        color: 'bg-gray-400', 
        gradient: 'from-gray-500 to-gray-600', 
        icon: 'fas fa-circle',
        glow: 'shadow-gray-500/25',
        pulse: 'animate-pulse'
    };
};

// ServerLogs Component (Server-specific log view with dynamic categories)
export function ServerLogs({ server, db, setModalMessage }) {
    const [logs, setLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(true);
    const [errorLogs, setErrorLogs] = useState(null);
    const [logCategories, setLogCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState('All'); // Default to 'All' category
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch log categories for the specific server
    useEffect(() => {
        if (!db || !server?.id) {
            setErrorLogs("Firestore database or server ID not available for categories.");
            setLoadingLogs(false);
            return;
        }

        const categoriesCollectionRef = collection(db, `servers/${server.id}/logCategories`);
        const q = query(categoriesCollectionRef, orderBy('name', 'asc')); // Order categories alphabetically

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedCategories = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            // Add an "All" category option at the beginning
            setLogCategories([{ id: 'all', name: 'All', icon: 'fas fa-th-large', color: '#4d9eff' }, ...fetchedCategories]);
        }, (err) => {
            console.error("Error fetching log categories:", err);
            setErrorLogs(`Failed to load log categories: ${err.message}`);
            setModalMessage(`Failed to load log categories: ${err.message}`);
        });

        return () => unsubscribe();
    }, [db, server?.id, setModalMessage]);

    // Fetch logs based on active category, server ID, and search term
    useEffect(() => {
        if (!db || !server?.id) {
            setErrorLogs("Firestore database or server ID not available for logs.");
            setLoadingLogs(false);
            return;
        }

        setLoadingLogs(true);
        setErrorLogs(null);

        let logsQueryRef = collection(db, 'logs'); // Assuming a global 'logs' collection
        
        // Filter by server ID (crucial for server-specific logs)
        logsQueryRef = query(logsQueryRef, where('serverId', '==', server.id));

        // Filter by category
        if (activeCategory && activeCategory !== 'All') {
            logsQueryRef = query(logsQueryRef, where('category', '==', activeCategory));
        }

        // Order by timestamp (descending for latest first)
        logsQueryRef = query(logsQueryRef, orderBy('timestamp', 'desc'));
        
        // Optional: limit for performance
        // logsQueryRef = query(logsQueryRef, limit(100)); 

        const unsubscribe = onSnapshot(logsQueryRef, (snapshot) => {
            let fetchedLogs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Client-side search filtering
            if (searchTerm) {
                const searchTermLower = searchTerm.toLowerCase();
                fetchedLogs = fetchedLogs.filter(log =>
                    (log.message && log.message.toLowerCase().includes(searchTermLower)) ||
                    (log.resource && log.resource.toLowerCase().includes(searchTermLower)) ||
                    (log.topic && log.topic.toLowerCase().includes(searchTermLower)) ||
                    (log.userId && log.userId.toLowerCase().includes(searchTermLower))
                );
            }

            setLogs(fetchedLogs);
            setLoadingLogs(false);
        }, (err) => {
            console.error("Error fetching logs:", err);
            setErrorLogs(`Failed to load logs: ${err.message}`);
            setModalMessage(`Failed to load logs: ${err.message}`);
            setLoadingLogs(false);
        });

        return () => unsubscribe();
    }, [db, server?.id, activeCategory, searchTerm, setModalMessage]); // Dependencies updated

    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value);
    }, []);

    if (loadingLogs) {
        return (
            <div className="glass-card rounded-2xl h-full flex items-center justify-center p-12 animate-fadeIn overflow-hidden">
                <div className="text-center relative">
                    {/* Enhanced loading animations with multiple layers */}
                    <div className="relative mb-8">
                        {/* Outer rotating ring */}
                        <div className="animate-spin rounded-full h-24 w-24 border-4 border-blue-500/20 border-t-blue-500 mx-auto animate-glow"></div>
                        {/* Inner pulsing circle */}
                        <div className="absolute inset-4 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 animate-pulse"></div>
                        {/* Center icon with float animation */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <i className="fas fa-clipboard-list text-2xl text-blue-400 animate-float"></i>
                        </div>
                        {/* Floating particles */}
                        <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-blue-400/60 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="absolute bottom-2 left-2 w-2 h-2 rounded-full bg-purple-400/60 animate-bounce" style={{ animationDelay: '0.8s' }}></div>
                        <div className="absolute top-2 left-2 w-1 h-1 rounded-full bg-cyan-400/60 animate-bounce" style={{ animationDelay: '1.2s' }}></div>
                    </div>
                    <div className="space-y-4">
                        <h3 className="text-3xl font-bold gradient-text animate-slideInLeft">Loading Server Logs</h3>
                        <p className="text-gray-300 text-lg animate-fadeIn" style={{ animationDelay: '0.3s' }}>
                            Gathering {server?.name || 'server'} activity data...
                        </p>
                        <div className="flex items-center justify-center space-x-3 animate-slideInLeft" style={{ animationDelay: '0.5s' }}>
                            <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce"></div>
                            <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                        {/* Progress indicators */}
                        <div className="mt-6 space-y-2 animate-fadeIn" style={{ animationDelay: '0.7s' }}>
                            <div className="flex items-center justify-center space-x-2 text-sm text-gray-400">
                                <i className="fas fa-database text-blue-400"></i>
                                <span>Connecting to Firestore...</span>
                            </div>
                            <div className="flex items-center justify-center space-x-2 text-sm text-gray-400">
                                <i className="fas fa-server text-purple-400"></i>
                                <span>Fetching server logs...</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (errorLogs) {
        return (
            <div className="glass-card rounded-2xl h-full flex items-center justify-center p-12 animate-fadeIn">
                <div className="text-center">
                    <div className="relative mb-8">
                        <div className="w-24 h-24 bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-2xl flex items-center justify-center mx-auto shadow-lg animate-bounce">
                            <i className="fas fa-exclamation-triangle text-4xl text-red-400 animate-pulse"></i>
                        </div>
                        <div className="absolute -inset-2 bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-2xl animate-ping"></div>
                        {/* Error particles */}
                        <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-400/60 animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                        <div className="absolute bottom-1 left-1 w-2 h-2 rounded-full bg-orange-400/60 animate-bounce" style={{ animationDelay: '0.7s' }}></div>
                    </div>
                    <div className="space-y-4">
                        <h3 className="text-3xl font-bold gradient-text animate-slideInLeft">Connection Error</h3>
                        <p className="text-gray-300 text-lg mb-6 animate-fadeIn" style={{ animationDelay: '0.2s' }}>{errorLogs}</p>
                        <div className="space-y-2 animate-slideInLeft" style={{ animationDelay: '0.4s' }}>
                            <p className="text-sm text-gray-400">Please check your connection and try again</p>
                            <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
                                <i className="fas fa-database"></i>
                                <span>Firestore connection and server configuration</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="glass-card rounded-2xl h-full flex flex-col p-6 animate-fadeIn overflow-hidden">
            {/* Ultra-Enhanced Header Section with Floating Elements */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 animate-slideInLeft flex-shrink-0">
                <div className="mb-4 lg:mb-0 relative">
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl blur-xl animate-pulse"></div>
                    <div className="relative">
                        <h2 className="text-3xl font-black gradient-text flex items-center mb-3 animate-slideInLeft">
                            <div className="relative mr-4">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center shadow-lg animate-float">
                                    <i className="fas fa-clipboard-list text-2xl text-blue-400 animate-pulse"></i>
                                </div>
                                <div className="absolute -inset-1 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 animate-ping opacity-75"></div>
                            </div>
                            <div>
                                {server?.name || 'Server'} Logs
                                <div className="h-1 w-0 bg-gradient-to-r from-blue-400 to-purple-400 animate-[width-expand_2s_ease-out_forwards] rounded-full"></div>
                            </div>
                        </h2>
                        <div className="flex items-center space-x-4 animate-fadeIn" style={{ animationDelay: '0.3s' }}>
                            <div className="flex items-center space-x-2 text-gray-300">
                                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                                <span className="text-sm font-medium">Real-time monitoring active</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-500 text-xs">
                                <i className="fas fa-sync-alt animate-spin" style={{ animationDuration: '3s' }}></i>
                                <span>Auto-refresh enabled</span>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Ultra-Interactive Search Input */}
                <div className="relative group animate-bounceIn" style={{ animationDelay: '0.5s' }}>
                    <div className="glass-card rounded-xl p-3 border border-white/20 group-hover:border-blue-400/50 transition-all duration-500 shadow-lg hover:shadow-2xl hover:shadow-blue-500/25">
                        <div className="relative min-w-[280px]">
                            <input
                                type="text"
                                name="search"
                                placeholder="Search server logs..."
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 pr-12 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 hover:bg-white/10 focus:bg-white/10"
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                                <i className="fas fa-search text-gray-400 group-hover:text-blue-400 transition-colors duration-300"></i>
                                {searchTerm && (
                                    <button 
                                        onClick={() => setSearchTerm('')}
                                        className="w-5 h-5 rounded-full bg-gray-600/50 hover:bg-red-500/50 flex items-center justify-center transition-all duration-300 hover:scale-110"
                                    >
                                        <i className="fas fa-times text-xs text-gray-400 hover:text-white"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Glow effect */}
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl opacity-0 group-hover:opacity-100 blur-xl transition-all duration-500 pointer-events-none"></div>
                </div>
            </div>

            {/* Ultra-Enhanced Log Categories Section */}
            <div className="relative overflow-hidden glass-card rounded-xl p-6 mb-6 border border-white/20 hover:border-purple-400/50 transition-all duration-500 animate-slideInLeft flex-shrink-0" style={{ animationDelay: '0.2s' }}>
                {/* Background animation */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 via-blue-600/3 to-purple-600/5 opacity-0 hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/2 to-transparent -translate-x-full animate-shimmer"></div>
                
                <div className="relative">
                    <h3 className="text-xl font-black text-white mb-6 flex items-center animate-slideInLeft">
                        <div className="relative mr-3">
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center shadow-lg animate-heartbeat">
                                <i className="fas fa-filter text-purple-400 text-lg"></i>
                            </div>
                            <div className="absolute -inset-1 rounded-lg bg-gradient-to-br from-purple-500/10 to-blue-500/10 animate-ping opacity-50"></div>
                        </div>
                        <div>
                            Log Categories
                            <div className="h-0.5 w-0 bg-gradient-to-r from-purple-400 to-blue-400 animate-[width-expand_1.5s_ease-out_0.3s_forwards] rounded-full"></div>
                        </div>
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                        {logCategories.map((categoryItem, index) => (
                            <button
                                key={categoryItem.id}
                                onClick={() => setActiveCategory(categoryItem.name)}
                                className={`group relative overflow-hidden glass-card rounded-xl p-4 flex flex-col items-center justify-center text-center transition-all duration-700 hover:scale-105 hover:-translate-y-2 border border-white/10 animate-fadeIn hover:shadow-xl ${
                                    activeCategory === categoryItem.name 
                                        ? 'ring-2 ring-blue-500/50 bg-blue-500/10 shadow-blue-500/25' 
                                        : 'hover:ring-1 hover:ring-white/20 hover:bg-white/5'
                                }`}
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                {/* Shimmer overlay */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                
                                {/* Gradient background overlay on hover */}
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/5 to-cyan-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                
                                <div className="relative">
                                    <div className="relative mb-3">
                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-lg" 
                                             style={{background: `linear-gradient(135deg, ${categoryItem.color || '#4d9eff'}, ${categoryItem.color || '#316dae'})`}}>
                                            <i 
                                                className={`${categoryItem.icon} text-white text-lg transition-all duration-300 ${
                                                    activeCategory === categoryItem.name ? 'animate-pulse' : 'group-hover:animate-bounce'
                                                }`}
                                            />
                                        </div>
                                        {/* Animated ring */}
                                        <div className={`absolute -inset-1 rounded-lg opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300`}
                                             style={{background: `${categoryItem.color || '#4d9eff'}20`}}></div>
                                    </div>
                                    <p className={`text-sm font-bold transition-colors duration-300 ${
                                        activeCategory === categoryItem.name 
                                            ? 'text-white' 
                                            : 'text-gray-300 group-hover:text-white'
                                    }`}>
                                        {categoryItem.name}
                                    </p>
                                    {/* Progress bar effect */}
                                    <div className="h-0.5 w-0 bg-gradient-to-r from-blue-400 to-purple-400 group-hover:w-full transition-all duration-700 rounded-full mt-2"></div>
                                </div>
                            </button>
                        ))}
                        {logCategories.length === 0 && !loadingLogs && (
                            <div className="col-span-full glass-card rounded-xl p-8 text-center animate-fadeIn">
                                <div className="relative mb-6">
                                    <div className="w-16 h-16 mx-auto rounded-xl bg-gradient-to-br from-gray-600/20 to-gray-700/20 flex items-center justify-center animate-float">
                                        <i className="fas fa-folder-open text-3xl text-gray-400/50 animate-pulse"></i>
                                    </div>
                                    <div className="absolute -inset-2 bg-gradient-to-br from-gray-600/10 to-gray-700/10 rounded-xl animate-ping opacity-50"></div>
                                </div>
                                <h4 className="text-lg font-bold gradient-text mb-3">No Categories Found</h4>
                                <p className="text-gray-400 mb-2">No log categories defined for this server</p>
                                <p className="text-gray-500 text-sm">You can add them in server settings</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Ultra-Enhanced Logs Display Section */}
            <div className="flex-1 relative overflow-hidden glass-card rounded-xl p-6 border border-white/20 hover:border-green-400/50 transition-all duration-500 flex flex-col animate-fadeIn min-h-0" style={{ animationDelay: '0.4s' }}>
                {/* Background animation */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-600/5 via-cyan-600/3 to-green-600/5 opacity-0 hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/2 to-transparent -translate-x-full animate-shimmer"></div>
                
                <div className="relative flex-shrink-0">
                    <h3 className="text-xl font-black text-white mb-6 flex items-center justify-between animate-slideInLeft">
                        <div className="flex items-center">
                            <div className="relative mr-3">
                                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500/20 to-cyan-500/20 flex items-center justify-center shadow-lg animate-heartbeat">
                                    <i className="fas fa-list-alt text-green-400 text-lg"></i>
                                </div>
                                <div className="absolute -inset-1 rounded-lg bg-gradient-to-br from-green-500/10 to-cyan-500/10 animate-ping opacity-50"></div>
                            </div>
                            <div>
                                Log Entries
                                <div className="h-0.5 w-0 bg-gradient-to-r from-green-400 to-cyan-400 animate-[width-expand_1.5s_ease-out_0.3s_forwards] rounded-full"></div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center space-x-2 text-sm text-gray-400">
                                <i className="fas fa-filter text-green-400"></i>
                                <span>Category: <span className="text-blue-400 font-semibold">{activeCategory}</span></span>
                            </div>
                            <div className="relative group">
                                <span className="px-4 py-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-400 text-sm rounded-xl border border-blue-500/30 font-bold group-hover:from-blue-500/30 group-hover:to-purple-500/30 transition-all duration-300 hover:scale-105 shadow-lg">
                                    {logs.length} entries
                                </span>
                                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl opacity-0 group-hover:opacity-100 blur-sm transition-all duration-300"></div>
                            </div>
                        </div>
                    </h3>
                </div>
                
                <div className="flex-1 overflow-y-auto custom-scrollbar relative min-h-0">
                    {logs.length === 0 ? (
                        <div className="glass-card rounded-xl p-8 text-center animate-fadeIn">
                            <div className="relative mb-6">
                                {/* Animated background rings */}
                                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-green-500/10 to-cyan-500/10 animate-ping" style={{ animationDuration: '3s' }}></div>
                                <div className="absolute inset-4 rounded-full bg-gradient-to-r from-green-500/5 to-cyan-500/5 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }}></div>
                                
                                <div className="relative w-20 h-20 mx-auto rounded-xl bg-gradient-to-br from-green-600/20 to-cyan-600/20 flex items-center justify-center animate-float">
                                    <i className="fas fa-search text-4xl text-green-400 animate-pulse"></i>
                                </div>
                            </div>
                            <h4 className="text-xl font-bold gradient-text mb-3 animate-slideInLeft">No Logs Found</h4>
                            <p className="text-gray-300 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
                                {activeCategory === 'All' 
                                    ? 'No logs available for this server'
                                    : `No logs found in "${activeCategory}" category`
                                }
                            </p>
                            {searchTerm && (
                                <p className="text-gray-400 text-sm mt-2 animate-fadeIn" style={{ animationDelay: '0.4s' }}>
                                    Try adjusting your search term: "{searchTerm}"
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {logs.map((log, index) => {
                                const topicData = getTopicColorAndIcon(log.topic);
                                return (
                                    <div 
                                        key={log.id} 
                                        className="group relative overflow-hidden glass-card rounded-xl p-4 hover:bg-white/10 transition-all duration-700 hover:scale-[1.01] hover:-translate-y-1 border border-white/10 hover:border-green-400/50 animate-fadeIn hover:shadow-xl hover:shadow-green-500/25"
                                        style={{ animationDelay: `${index * 0.03}s` }}
                                    >
                                        {/* Enhanced hover effects */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                        <div className="absolute inset-0 bg-gradient-to-br from-green-600/10 via-cyan-600/5 to-green-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                        
                                        <div className="relative">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-3 flex-grow min-w-0">
                                                    {/* Ultra-interactive topic indicator */}
                                                    <div className="relative flex-shrink-0">
                                                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${topicData.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 ${topicData.pulse}`}>
                                                            <i className={`${topicData.icon} text-white text-sm`}></i>
                                                        </div>
                                                        <div className={`absolute -inset-1 rounded-xl ${topicData.color}/20 opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300`}></div>
                                                    </div>
                                                    
                                                    <div className="min-w-0 flex-grow">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <span className="text-white font-bold group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-green-400 group-hover:to-cyan-400 group-hover:bg-clip-text transition-all duration-500 truncate">
                                                                {log.resource || 'System'}
                                                            </span>
                                                            <span className="px-2 py-1 bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-400 text-xs rounded-lg border border-blue-500/30 font-semibold hover:from-blue-500/30 hover:to-purple-500/30 transition-all duration-300 flex-shrink-0">
                                                                {log.topic || 'general'}
                                                            </span>
                                                            {log.category && log.category !== activeCategory && (
                                                                <span className="px-2 py-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-400 text-xs rounded-lg border border-purple-500/30 font-semibold hover:from-purple-500/30 hover:to-pink-500/30 transition-all duration-300 flex-shrink-0">
                                                                    {log.category}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <p className="text-xs text-gray-400 font-medium">{formatTimestamp(log.timestamp)}</p>
                                                            <span className="text-xs text-green-400 font-bold bg-green-500/10 px-2 py-1 rounded-md border border-green-500/20">
                                                                {getRelativeTime(log.timestamp)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                {log.userId && (
                                                    <div className="flex-shrink-0 ml-3">
                                                        <span className="text-xs text-gray-400 font-mono bg-gray-500/20 px-3 py-1.5 rounded-lg border border-gray-500/30 hover:bg-gray-500/30 transition-all duration-300">
                                                            <i className="fas fa-user mr-1 text-gray-500"></i>
                                                            {log.userId}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="pl-3 border-l-2 border-gradient-to-b border-green-500/20 group-hover:border-green-500/40 transition-all duration-300">
                                                <p className="text-gray-200 text-sm leading-relaxed break-words group-hover:text-white transition-colors duration-300">
                                                    {log.message || 'No message content'}
                                                </p>
                                            </div>
                                            
                                            {/* Progress bar effect */}
                                            <div className="h-0.5 w-0 bg-gradient-to-r from-green-400 to-cyan-400 group-hover:w-full transition-all duration-700 rounded-full mt-3"></div>
                                        </div>
                                        
                                        {/* Floating particles */}
                                        <div className="absolute top-1 right-1 w-1 h-1 rounded-full bg-green-400/60 opacity-0 group-hover:opacity-100 animate-bounce transition-opacity duration-300" style={{ animationDelay: '0.2s' }}></div>
                                        <div className="absolute bottom-1 left-1 w-1 h-1 rounded-full bg-cyan-400/60 opacity-0 group-hover:opacity-100 animate-bounce transition-opacity duration-300" style={{ animationDelay: '0.8s' }}></div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ServerLogs;